import { __awaiter, __generator, __read, __spreadArray } from "tslib";
import { FileType } from '@office-start/document-types';
import { getErrorHandler } from '@office-start/error-handler';
import { createGuid } from '@office-start/guid-generator';
import { KnownInstrumentationEvents, } from '@office-start/instrumentation-types';
import { IDLE_VERYLOW_PRIORITY, scheduleWork, } from '@office-start/scheduler-service';
import { logError, logFeature } from '@office-start/telemetry-service';
import { getRequestIdFromResponse, httpGet } from '@office-start/xhr-service';
import { clientRequestIdHeader, excludeAppsParam, excludedApps, groupParam, headerDiagnostics, localeParam, } from '../models/Constants';
import { getServerDebugInfoString } from '../utilities/getServerDebugInfoString';
import { createRequestConfig } from './createRequestConfig';
import { parseRecentResponse } from './parseRecentResponse';
import { parseSettings } from './parseSettings';
// the Recent API only supports specific app types, others fails the request with 400 - Bad Request
// List of allowed types can be found here: https://o365exchange.visualstudio.com/O365%20Core/_git/Griffin?path=%2Fsources%2Fdev%2FEdgeworth%2Fdocs%2FRecent.md&_a=preview&version=GBmaster
var getRecentAppParam = function (fileType) {
    switch (fileType) {
        case FileType.Word:
            return 'Word';
        case FileType.PowerPoint:
            return 'PowerPoint';
        case FileType.Excel:
            return 'Excel';
        case FileType.Video:
        case FileType.Stream:
        case FileType.StreamsVideo:
            return 'Video';
        case FileType.Visio:
        case FileType.VisioSoftwareWac:
        case FileType.VisioBusinessWac:
        case FileType.VisioFlowChartWac:
        case FileType.VisioGeneralWac:
        case FileType.VisioNetworkWac:
        case FileType.VisioStartWac:
            return 'Visio';
        default:
            return null;
    }
};
var fetchRequestIndex = 0;
export function fetchRecent(apiOptions) {
    return __awaiter(this, void 0, void 0, function () {
        var aadAppId, correlationId, edgeworthApiRootPath, edgeworthTopicsEnabled, enableBackgroundSync, getAccessToken, iceApplicationId, jwtAuthEnabled, meetingObjectsEnabled, objectId, officeHomeApiRoot, preferredCulture, recentApiPath, recentItemsCount, settingsEnabled, substrateApiEndpoint, tenantId, currentPage, filterFileType, requestId, endpoint, requestConfig, recentApiAppParams, appFilterValues, groupArray, groupString;
        return __generator(this, function (_a) {
            aadAppId = apiOptions.aadAppId, correlationId = apiOptions.correlationId, edgeworthApiRootPath = apiOptions.edgeworthApiRootPath, edgeworthTopicsEnabled = apiOptions.edgeworthTopicsEnabled, enableBackgroundSync = apiOptions.enableBackgroundSync, getAccessToken = apiOptions.getAccessToken, iceApplicationId = apiOptions.iceApplicationId, jwtAuthEnabled = apiOptions.jwtAuthEnabled, meetingObjectsEnabled = apiOptions.meetingObjectsEnabled, objectId = apiOptions.objectId, officeHomeApiRoot = apiOptions.officeHomeApiRoot, preferredCulture = apiOptions.preferredCulture, recentApiPath = apiOptions.recentApiPath, recentItemsCount = apiOptions.recentItemsCount, settingsEnabled = apiOptions.settingsEnabled, substrateApiEndpoint = apiOptions.substrateApiEndpoint, tenantId = apiOptions.tenantId, currentPage = apiOptions.currentPage, filterFileType = apiOptions.filterFileType;
            requestId = createGuid();
            endpoint = "".concat(substrateApiEndpoint).concat(edgeworthApiRootPath).concat(recentApiPath, "?top=").concat(recentItemsCount, "&").concat(localeParam).concat(preferredCulture, "&").concat(excludeAppsParam).concat(excludedApps);
            requestConfig = createRequestConfig(objectId, tenantId, iceApplicationId, currentPage, aadAppId, requestId);
            recentApiAppParams = new Set();
            filterFileType &&
                filterFileType.forEach(function (param) {
                    var recentApiAppParam = getRecentAppParam(param);
                    if (!recentApiAppParam) {
                        return;
                    }
                    recentApiAppParams.add(recentApiAppParam);
                });
            appFilterValues = recentApiAppParams
                ? Array.from(recentApiAppParams.values()).join(',')
                : '';
            endpoint = appFilterValues
                ? "".concat(endpoint, "&apps=").concat(appFilterValues)
                : endpoint;
            groupArray = __spreadArray(__spreadArray(__spreadArray([], __read((meetingObjectsEnabled ? ['meetingobjects'] : [])), false), __read((edgeworthTopicsEnabled ? ['topics'] : [])), false), __read((settingsEnabled ? ['settings'] : [])), false);
            groupString = groupArray.join(',');
            if (groupArray.length > 0) {
                endpoint = "".concat(endpoint, "&").concat(groupParam).concat(groupString);
            }
            return [2 /*return*/, httpGet(KnownInstrumentationEvents.EdgeworthSubstrateRecentRequest, "EdgeworthSubstrateRecent" /* KnownRequestInstrumentationEvents.EdgeworthSubstrateRecentRequest */, endpoint, requestConfig, jwtAuthEnabled, getAccessToken, officeHomeApiRoot, correlationId, 'Substrate', {
                    populateRequestEvent: function (response, params) {
                        params["EventType" /* InstrumentationParam.EventType */] = 'Request';
                        params["RequestId" /* InstrumentationParam.RequestId */] =
                            getRequestIdFromResponse(response) || '';
                        params["DebugInfo" /* InstrumentationParam.DebugInfo */] =
                            getServerDebugInfoString(response);
                        params["Index" /* InstrumentationParam.Index */] =
                            (fetchRequestIndex++).toString();
                        params["ImpressionId" /* InstrumentationParam.ImpressionId */] =
                            (response.headers &&
                                response.headers[clientRequestIdHeader]) ||
                                requestId;
                        params["Diagnostics" /* InstrumentationParam.Diagnostics */] =
                            response.headers && response.headers[headerDiagnostics];
                        var featureParams = params;
                        logFeature({
                            featureName: "Edgeworth" /* KnownFeatureNames.Edgeworth */,
                            eventName: "EdgeworthSubstrateRecent" /* KnownRequestInstrumentationEvents.EdgeworthSubstrateRecentRequest */,
                            params: featureParams,
                        });
                        delete params["EventType" /* InstrumentationParam.EventType */];
                    },
                })
                    .then(function (response) {
                    if (!response || !response.data) {
                        logError({
                            eventName: 'FetchRecentError',
                            errorType: 'Invalid_Response',
                        });
                        throw new Error('Invalid recent response data');
                    }
                    if (enableBackgroundSync) {
                        setTimeout(function () {
                            scheduleWork(function () {
                                var newEndpoint = endpoint
                                    .replace('substrate.office.com', 'substrate-sdf.office.com')
                                    .replace('recommended', 'iroc');
                                httpGet(KnownInstrumentationEvents.EdgeworthSubstrateRecentRequest, "EdgeworthSubstrateRecent" /* KnownRequestInstrumentationEvents.EdgeworthSubstrateRecentRequest */, newEndpoint, requestConfig, jwtAuthEnabled, getAccessToken, officeHomeApiRoot, correlationId, 'Substrate');
                            }, IDLE_VERYLOW_PRIORITY);
                        }, 10000);
                    }
                    return {
                        settings: parseSettings(response.data),
                        data: parseRecentResponse(response.data),
                        impressionId: (response.headers &&
                            response.headers[clientRequestIdHeader]) ||
                            requestId,
                    };
                })
                    .catch(function (error) {
                    getErrorHandler().handleError(error, "Bn3rc" /* ErrorId.Bn3rc */);
                    throw error;
                })];
        });
    });
}
//# sourceMappingURL=fetchRecent.js.map